/**
 * Agent router + prompt DAG — admin overview and test
 * Route: /agent
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { hubService, type AgentRouteResult } from '../services/hub.service';

const DEFAULT_SYSTEM = 'chatbot';

type TabId = 'overview' | 'test';

export function AgentControl() {
  const { getToken } = useAuth();
  const [tab, setTab] = useState<TabId>('overview');

  const [message, setMessage] = useState('What are your opening hours?');
  const [userId, setUserId] = useState('admin-test-user');
  const [system, setSystem] = useState(DEFAULT_SYSTEM);
  const [botId, setBotId] = useState('default');
  const [channel, setChannel] = useState('');
  const [pageId, setPageId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AgentRouteResult | null>(null);

  const tabs = useMemo(
    () =>
      [
        { id: 'overview' as const, label: 'Overview & config' },
        { id: 'test' as const, label: 'Test /agent/route' },
      ] as const,
    [],
  );

  const runTest = useCallback(async () => {
    if (!message.trim()) {
      setError('Message is required.');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = getToken();
      const out = await hubService.agentRoute(
        {
          message: message.trim(),
          userId: userId.trim() || undefined,
          system: system.trim() || undefined,
          botId: botId.trim() || undefined,
          channel: channel.trim() || undefined,
          pageId: pageId.trim() || undefined,
        },
        token,
      );
      setResult(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [getToken, message, userId, system, botId, channel, pageId]);

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Agent (router + DAG)</h1>
        <p className="text-gray-600 mb-6">
          Hub module <code className="text-xs bg-gray-100 px-1 rounded">POST /api/agent/route</code> runs the semantic
          router, then the prompt DAG and executors (RAG, tools, workflow). Session state is stored{' '}
          <strong>only in Redis</strong> (<code className="text-xs">agent:ses:*</code> keys); the hub must have{' '}
          <code className="text-xs">REDIS_URL</code> set.
        </p>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                tab === t.id
                  ? 'bg-white border border-b-0 border-gray-200 -mb-px text-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="space-y-6 text-sm text-gray-800">
            <section className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 mb-2">Router prompt (LLM JSON decision)</h2>
              <ul className="list-disc pl-5 space-y-1 text-gray-700">
                <li>
                  Template name: <code className="text-xs bg-gray-50 px-1">agent_router_system</code>
                </li>
                <li>
                  Scope: <code className="text-xs bg-gray-50 px-1">botId = __system__</code> and your{' '}
                  <code className="text-xs">system</code> (e.g. chatbot) — same as other system prompts.
                </li>
                <li>
                  Manage under{' '}
                  <Link to="/ai-config" className="text-blue-600 hover:underline">
                    AI Config → Prompt Templates
                  </Link>{' '}
                  (system-wide) or per-bot{' '}
                  <span className="text-gray-600">Bot → Prompt Templates</span> if you add bot-specific overrides.
                </li>
              </ul>
            </section>

            <section className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 mb-2">Prompt DAG (per-node templates & context)</h2>
              <p className="text-gray-700 mb-2">
                Execution uses DB-backed prompts and context entities. Typical template names:
              </p>
              <ul className="list-disc pl-5 space-y-1 font-mono text-xs text-gray-800">
                <li>dag_fallback, dag_intent, dag_slot, dag_clarification, dag_workflow_tool</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Context names include <code className="text-xs bg-gray-50 px-1">dag_route_map</code> (route → node
                mapping), <code className="text-xs bg-gray-50 px-1">fallback_system</code>,{' '}
                <code className="text-xs bg-gray-50 px-1">intent_system</code>,{' '}
                <code className="text-xs bg-gray-50 px-1">slot_system</code>, etc. Configure via{' '}
                <strong>Bot → Context</strong> (per bot) or extend system defaults in code.
              </p>
            </section>

            <section className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 mb-2">Related env (hub)</h2>
              <ul className="list-disc pl-5 space-y-1 font-mono text-xs text-gray-800">
                <li>REDIS_URL — required for agent session memory</li>
                <li>AGENT_MEMORY_TTL_SEC</li>
                <li>AGENT_DEFAULT_SYSTEM, AGENT_DEFAULT_BOT_ID</li>
                <li>AGENT_PLANNER_DELEGATION_ENABLED, AGENT_PLANNER_DELEGATE_WORKFLOW, AGENT_PLANNER_DELEGATE_SERVICES</li>
                <li>MCP_SERVER_URL — tool path</li>
              </ul>
            </section>

            <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-950">
              <p className="font-medium mb-1">Agent planner is separate</p>
              <p className="text-sm">
                Structured planner loop lives under{' '}
                <Link to="/agent-planner" className="text-blue-700 hover:underline">
                  Agent planner
                </Link>
                . It can be invoked from the agent when delegation env flags match (workflow / configured services).
              </p>
            </section>
          </div>
        )}

        {tab === 'test' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Message *</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                  disabled={loading}
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">userId (session key)</span>
                  <input
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={loading}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">system</span>
                  <input
                    value={system}
                    onChange={(e) => setSystem(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={loading}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">botId</span>
                  <input
                    value={botId}
                    onChange={(e) => setBotId(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={loading}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">channel (optional)</span>
                  <input
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    placeholder="messenger"
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={loading}
                  />
                </label>
                <label className="flex flex-col gap-1 sm:col-span-2">
                  <span className="text-xs text-gray-500">pageId (optional, with channel)</span>
                  <input
                    value={pageId}
                    onChange={(e) => setPageId(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    disabled={loading}
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => void runTest()}
                disabled={loading}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Running…' : 'Run /api/agent/route'}
              </button>
            </div>
            {error && (
              <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm whitespace-pre-wrap">{error}</div>
            )}
            {result && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
                <p className="text-xs font-semibold text-green-800 mb-2">response</p>
                <pre className="whitespace-pre-wrap text-gray-900 text-sm font-sans">{result.response}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
